import { useEffect, useRef, useState } from "react";
import { fq, toHalf, toInt, toQty, yenI } from "../lib/num.js";

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> & {
  /** int＝円（桁区切りで見せる）／dec＝数量（小数2桁まで） */
  kind: "int" | "dec";
  value: number | null;
  onChange: (n: number | null) => void;
  /** 0 を「0」、小数を「24.5」の形で見せる（dec の既定は 0 を空・小数2桁＝数量の欄。窓付封筒の mm のように 0 に意味がある欄で使う） */
  showZero?: boolean | undefined;
};

const format = (kind: "int" | "dec", v: number | null, showZero = false): string => {
  if (v == null) return "";
  if (showZero) return kind === "int" ? yenI(v) || "0" : String(Math.round(v * 100) / 100);
  return kind === "int" ? (v ? yenI(v) : "") : fq(v);
};

/**
 * 数字の欄。日本語入力のままでも半角に直し（docs/10 §5.2）、欄を出るときに桁区切りで整える。
 * 打っている間は打った文字をそのまま見せ、値だけ親に渡す
 */
export function NumInput({ kind, value, onChange, onBlur, onCompositionEnd, className, showZero = false, ...rest }: Props) {
  const [text, setText] = useState(() => format(kind, value, showZero));
  const focused = useRef(false);
  const comp = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(format(kind, value, showZero));
  }, [kind, value, showZero]);
  const commit = (raw: string) => {
    const t = toHalf(raw, kind);
    setText(t);
    if (t === "" || t === "-") onChange(null);
    else onChange(kind === "int" ? toInt(t) : toQty(t));
  };
  return (
    <input
      {...rest}
      className={"ce r num " + (className ?? "")}
      type="text"
      inputMode={kind === "int" ? "numeric" : "decimal"}
      value={text}
      onFocus={(e) => {
        focused.current = true;
        rest.onFocus?.(e);
      }}
      onCompositionStart={() => {
        comp.current = true;
      }}
      onCompositionEnd={(e) => {
        comp.current = false;
        commit(e.currentTarget.value);
        onCompositionEnd?.(e);
      }}
      onChange={(e) => {
        if (comp.current) setText(e.target.value);
        else commit(e.target.value);
      }}
      onBlur={(e) => {
        focused.current = false;
        setText(format(kind, value, showZero));
        onBlur?.(e);
      }}
    />
  );
}
